import { useState } from "react";
import { Pencil, Trash2, Plus, Eye, EyeOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, resolveImage, errorText } from "@/lib/api";
import { useAllBrands } from "@/components/Yachts";
import { PrimaryButton, GhostButton } from "./fields";
import BrandForm from "./BrandForm";

export default function BrandsManager() {
  const qc = useQueryClient();
  const { data: brands = [] } = useAllBrands();
  const [editing, setEditing] = useState(null);

  const refresh = () => qc.invalidateQueries();

  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.name} and all ${b.model_count} of its models?`)) return;
    try {
      await api.delete(`/admin/brands/${b.id}`);
      toast.success("Brand deleted.");
      refresh();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const toggleVisible = async (b) => {
    try {
      await api.put(`/admin/brands/${b.id}`, { ...b, visible: b.visible === false });
      toast.success(b.visible === false ? `${b.name} is now visible on the website.` : `${b.name} hidden from the website.`);
      refresh();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const resetCatalog = async () => {
    if (!window.confirm("Restore the default catalogue? This replaces ALL brands, models and hero slides with the official Modi Marine data (Sunseeker, Frauscher, Pirelli, Besnati, Belassi). Uploaded files are kept. Continue?")) return;
    try {
      const { data } = await api.post("/admin/catalog/reset");
      toast.success(`Catalogue restored — ${data.brands} brands, ${data.yachts} models.`);
      refresh();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div data-testid="admin-brands-manager">
      <div className="mb-8 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.3em] text-mist" data-testid="admin-brands-count">{brands.length} brand partners</p>
        <div className="flex gap-2">
          <GhostButton onClick={resetCatalog} data-testid="admin-reset-catalog-button" className="inline-flex items-center gap-2" title="Replace everything with the official seed data">
            <RotateCcw size={12} /> Restore default catalogue
          </GhostButton>
          <PrimaryButton onClick={() => setEditing("new")} data-testid="admin-add-brand-button" className="inline-flex items-center gap-2">
            <Plus size={12} /> Add brand
          </PrimaryButton>
        </div>
      </div>
      <div className="divide-y divide-navy-ink/10 border border-navy-ink/10 bg-white/40">
        {brands.map((b) => (
          <div key={b.id} className={`flex flex-wrap items-center gap-5 p-4 ${b.visible === false ? "opacity-60" : ""}`} data-testid={`admin-brand-row-${b.slug}`} data-visible={b.visible !== false}>
            <img src={resolveImage(b.hero_image)} alt="" className={`h-16 w-28 shrink-0 object-cover ${b.visible === false ? "grayscale" : ""}`} />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {b.origin} · /yachts/{b.slug}
                {b.visible === false && <span className="ml-3 border border-red-700/40 px-2 py-0.5 text-red-700" data-testid={`admin-brand-row-${b.slug}-hidden-badge`}>Hidden from website</span>}
              </p>
              <p className="mt-1 font-serif text-xl text-navy-ink">{b.name}</p>
              <p className="mt-1 text-xs text-mist">{b.model_count} models · {b.tagline}</p>
            </div>
            <div className="flex gap-2">
              <GhostButton onClick={() => toggleVisible(b)} data-testid={`admin-brand-row-${b.slug}-toggle-visible`} className="inline-flex items-center gap-2" title={b.visible === false ? "Show on website" : "Hide from website"}>
                {b.visible === false ? <><EyeOff size={12} /> Hidden</> : <><Eye size={12} /> Live</>}
              </GhostButton>
              <GhostButton onClick={() => setEditing(b)} data-testid={`admin-brand-row-${b.slug}-edit`} className="inline-flex items-center gap-2"><Pencil size={12} /> Edit</GhostButton>
              <GhostButton onClick={() => remove(b)} data-testid={`admin-brand-row-${b.slug}-delete`} className="inline-flex items-center gap-2 hover:border-red-700 hover:text-red-700"><Trash2 size={12} /></GhostButton>
            </div>
          </div>
        ))}
      </div>
      {editing && <BrandForm open onClose={() => setEditing(null)} brand={editing === "new" ? null : editing} onSaved={refresh} />}
    </div>
  );
}
